import * as THREE from "three";
import type { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import type { HqMaterials } from "./hqMaterials";

/** The asset library's model helpers (assets.ts): loading with a deadline, a bounded pool, shadow casters, rest poses, freeing. */

/**
 * Loads a GLB, failing a download that stalls (no byte for `stallMs`) or trickles past `timeoutMs`, with one retry
 * (stallMs / TimeoutMs say why).
 */
export async function loadWithDeadline(loader: GLTFLoader, url: string, stallMs: number, timeoutMs: number): Promise<{ scene: THREE.Group; animations: THREE.AnimationClip[] }> {
  for (let attempt = 0; ; attempt++) {
    let stall: ReturnType<typeof setTimeout> | undefined, cap: ReturnType<typeof setTimeout> | undefined;
    try {
      return await new Promise((resolve, reject) => {
        const arm = () => { clearTimeout(stall); stall = setTimeout(() => reject(new Error(`no data for ${stallMs} ms`)), stallMs); };
        arm();
        cap = setTimeout(() => reject(new Error(`timed out after ${timeoutMs} ms`)), timeoutMs);
        loader.loadAsync(url, arm).then(resolve, reject);
      });
    } catch (err) {
      if (attempt >= 1) throw err;
    } finally {
      clearTimeout(stall); clearTimeout(cap);
    }
  }
}

/** Runs `work` over `items` with at most `width` in flight. */
export async function poolWork<T>(items: readonly T[], work: (item: T) => Promise<void>, width: number): Promise<void> {
  let next = 0;
  const lane = async (): Promise<void> => { while (next < items.length) await work(items[next++]); };
  await Promise.all(Array.from({ length: Math.min(width, items.length) }, lane));
}

/**
 * Which of a model's meshes cast a shadow. Every mesh used to, and each caster is a second draw call in the shadow
 * pass: a raider is ten meshes (arms, boots, shield, skirt...), so a wave of forty was eight hundred draws before a
 * pixel of it was lit. At the camera's distance a unit's shadow is a blob, and its body alone makes the same blob;
 * projectiles are gone before a shadow reads; flames, smoke and glows should never have cast one.
 */
export function shadowCaster(name: string, root: THREE.Object3D): (mesh: THREE.Mesh) => boolean {
  const noFx = (m: THREE.Mesh) => !NO_SHADOW_PARTS.test(m.name);
  if (/^proj_/.test(name)) return () => false;
  if (!/^(enemy|troop|boss)_/.test(name)) return noFx;
  let biggest: THREE.Mesh | null = null, most = -1;
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh || !noFx(m)) return;
    const n = m.geometry.getAttribute("position")?.count ?? 0;
    if (n > most) { most = n; biggest = m; }
  });
  return (m) => m === biggest;
}
/** Parts that never cast a shadow: fire, smoke, glows and effects. */
export const NO_SHADOW_PARTS = /flame|smoke|glow|spark|beam|halo|_fx|fx_/i;

/**
 * The rigs are exported with their parts at the first frame of `build` (storeys and roof at scale 0, scaffold up),
 * so a model that only plays `idle` would show its plinth alone. Write the last frame of `build` into the nodes as
 * the rest pose: that is the finished tower, and `build` still animates from its own first frame when played.
 */
export function bakeRestPose(root: THREE.Object3D, clips: readonly THREE.AnimationClip[]): void {
  const build = THREE.AnimationClip.findByName(clips as THREE.AnimationClip[], "build");
  if (!build) return;
  for (const track of build.tracks) {
    const dot = track.name.lastIndexOf(".");
    const nodeName = track.name.slice(0, dot), prop = track.name.slice(dot + 1);
    const node = root.getObjectByName(nodeName);
    if (!node) continue;
    const size = track.getValueSize();
    const last = track.values.slice(track.values.length - size);
    if (prop === "scale") node.scale.fromArray(last);
    else if (prop === "position") node.position.fromArray(last);
    else if (prop === "quaternion") node.quaternion.fromArray(last);
  }
}

/** Frees a model's geometry, materials (and the material module's references to them), textures and skeletons. */
export function disposeTree(root: THREE.Object3D, materials: HqMaterials): void {
  const geos = new Set<THREE.BufferGeometry>(), mats = new Set<THREE.Material>(), texs = new Set<THREE.Texture>();
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    geos.add(mesh.geometry);
    for (const m of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) mats.add(m);
    (mesh as THREE.SkinnedMesh).skeleton?.dispose();
  });
  for (const m of mats) for (const v of Object.values(m)) if ((v as THREE.Texture | null)?.isTexture) texs.add(v as THREE.Texture);
  materials.forget(mats);
  for (const g of geos) g.dispose();
  for (const m of mats) m.dispose();
  for (const t of texs) t.dispose();
}

/** The model names a manifest (`{"models": [...]}`) lists; none when it is missing or unreadable. */
export async function fetchModelList(url: string): Promise<string[]> {
  try {
    const res = await fetch(url);
    if (!res.ok) return [];
    const doc = (await res.json()) as { models?: string[] };
    return Array.isArray(doc.models) ? doc.models : [];
  } catch { return []; }
}

/** Gives every mesh under `root` its own copy of its material, multiplied by `tint`. */
export function tintTree(root: THREE.Object3D, tint: number): void {
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    const m = (mesh.material as THREE.MeshToonMaterial | THREE.MeshLambertMaterial).clone();
    m.color.multiply(new THREE.Color(tint));
    mesh.material = m;
  });
}

/** `url` served from the inlined store (AssetLibrary.inlined) when a key matches its path's end; else unchanged. */
export function resolveIn(store: Readonly<Record<string, string>> | null, url: string): string {
  if (store === null || url.startsWith("data:") || url.startsWith("blob:")) return url;
  const clean = url.replace(/^\.?\//, "");
  for (const key of Object.keys(store)) if (clean.endsWith(key)) return store[key];
  return url;
}
